import { canNavigate } from '@layouts/plugins/casl'
import { useCookie } from '@core/composable/useCookie'
import { useTrainerAccountStore } from '@/stores/trainerAccount'
import { useClientAccountStore } from '@/stores/clientAccount'
import { usePlatformAdminAccountStore } from '@/stores/platformAdminAccount'

export const setupGuards = router => {
  router.beforeEach(async to => {
    if (to.meta.public)
      return

    // Platform Admin area — isolated from Trainer/Client auth.
    const isAdminArea = !!to.meta.adminArea || String(to.path || '').startsWith('/admin')

    if (isAdminArea) {
      const adminStore = usePlatformAdminAccountStore()

      if (!adminStore.initialized)
        await adminStore.bootstrap()

      const adminLoggedIn = adminStore.isSignedIn || !!useCookie('adminSignedIn').value

      if (to.meta.unauthenticatedOnly) {
        if (adminLoggedIn)
          return { path: '/admin' }

        return undefined
      }

      if (!adminLoggedIn) {
        return {
          path: '/admin/login',
          query: {
            ...to.query,
            to: to.fullPath !== '/admin' ? to.fullPath : undefined,
          },
        }
      }

      return undefined
    }

    // Require trailing slash so '/clients' (trainer roster) is not treated as client area.
    const isClientArea = !!to.meta.clientArea || String(to.path || '').startsWith('/client/')

    if (isClientArea) {
      const clientStore = useClientAccountStore()

      if (!clientStore.initialized)
        await clientStore.bootstrap()

      const clientLoggedIn = clientStore.isSignedIn || !!useCookie('clientSignedIn').value

      if (to.meta.unauthenticatedOnly) {
        if (clientLoggedIn) {
          if (!clientStore.client && useCookie('clientSignedIn').value)
            await clientStore.fetchProfile().catch(() => null)

          return { path: '/client/today' }
        }

        return undefined
      }

      if (!clientLoggedIn) {
        return {
          path: '/client/login',
          query: {
            ...to.query,
            to: to.fullPath !== '/client/today' ? to.path : undefined,
          },
        }
      }

      if (!clientStore.client && useCookie('clientSignedIn').value)
        await clientStore.fetchProfile().catch(() => null)

      return undefined
    }

    const accountStore = useTrainerAccountStore()

    if (!accountStore.initialized)
      await accountStore.bootstrap()

    const isLoggedIn = accountStore.isSignedIn || !!useCookie('trainerSignedIn').value

    if (to.meta.unauthenticatedOnly) {
      if (isLoggedIn) {
        if (!accountStore.trainer && useCookie('trainerSignedIn').value)
          await accountStore.fetchProfile().catch(() => null)

        return accountStore.postLoginRedirectPath()
      }

      return undefined
    }

    if (!isLoggedIn) {
      return {
        name: 'login',
        query: {
          ...to.query,
          to: to.fullPath !== '/' ? to.path : undefined,
        },
      }
    }

    if (!accountStore.trainer && useCookie('trainerSignedIn').value)
      await accountStore.fetchProfile().catch(() => null)

    if ((accountStore.isSuspended || accountStore.isDeactivated) && !to.meta.allowRestricted && to.name !== 'account-restricted')
      return { name: 'account-restricted' }

    if (accountStore.isOnboarding && !to.meta.allowOnboarding && !to.meta.allowRestricted && to.name !== 'onboarding')
      return { name: 'onboarding' }

    // Materio demo CASL gate — skip when FitX session auth is in use.
    const isMaterioDemoSession = !!useCookie('accessToken').value
      && !useCookie('trainerSignedIn').value
      && !useCookie('clientSignedIn').value
      && !useCookie('adminSignedIn').value

    if (isMaterioDemoSession && !canNavigate(to) && to.matched.length) {
      return isLoggedIn
        ? { name: 'not-authorized' }
        : {
          name: 'login',
          query: {
            ...to.query,
            to: to.fullPath !== '/' ? to.path : undefined,
          },
        }
    }
  })
}
